import React, { memo, useCallback, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Grid,
  Menu,
  MenuItem,
} from "@mui/material";
import IpdTextField from "../../../../utils/IpdTextField";
import IpdDateField from "../../../../utils/IpdDateField";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import FormField from "../FormField";
import ConfigurableField from "../ConfigurableField";
import PhotoQrDialog from "../dialogs/PhotoQrDialog";
import {
  TITLES, GENDERS, PATIENT_TYPES, MARITAL_STATUSES, BLOOD_GROUPS,
  NATIONALITIES, RELIGIONS,
} from "../../constants/registrationConstants";
import { calcAge, dobFromAgeYears } from "../../utils/ageUtils";
import { CONFIGURED_SECTION_IDS } from "../../utils/sectionConfiguration";
import { syncPersonalMobileToContacts } from "../../utils/contactSync";
import { useSectionConfig } from "../../context/SectionConfigContext";

const S = CONFIGURED_SECTION_IDS;

function PersonalInfo({ patient, updateField, setPatient, errors = {} }) {
  const { isFieldActive, isSectionActive, config } = useSectionConfig();
  const contactsSectionActive = !config || isSectionActive(S.contacts);
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState(null);
  const [photoQrOpen, setPhotoQrOpen] = useState(false);
  const photoInputRef = useRef(null);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => updateField("photoUrl", String(r.result));
    r.readAsDataURL(f);
  };

  const onPhotoFromQr = useCallback(
    (dataUrl) => updateField("photoUrl", dataUrl),
    [updateField],
  );
  const closePhotoQr = useCallback(() => setPhotoQrOpen(false), []);

  const showPhoto = !config || isFieldActive(S.personal, "photoUrl");

  return (
    <Grid container spacing={2}>
      {showPhoto && (
        <Grid item xs={12} md={2}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <ButtonBase
              aria-label="Choose patient photo"
              aria-haspopup="menu"
              onClick={(e) => setPhotoMenuAnchor(e.currentTarget)}
              sx={{ borderRadius: "50%" }}
            >
              <Avatar
                src={patient.photoUrl || undefined}
                sx={{ width: 96, height: 96 }}
                alt="Patient photo"
              >
                {!patient.photoUrl && <PhotoCameraIcon />}
              </Avatar>
            </ButtonBase>
            <Button
              size="small"
              variant="text"
              aria-haspopup="menu"
              onClick={(e) => setPhotoMenuAnchor(e.currentTarget)}
            >
              Choose Photo
            </Button>
            {patient.photoUrl && (
              <Button
                size="small"
                variant="text"
                color="error"
                onClick={() => updateField("photoUrl", "")}
              >
                Remove Photo
              </Button>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onPhoto}
              aria-label="Upload patient photo"
            />
            <Menu
              anchorEl={photoMenuAnchor}
              open={Boolean(photoMenuAnchor)}
              onClose={() => setPhotoMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setPhotoMenuAnchor(null);
                  if (photoInputRef.current) photoInputRef.current.value = "";
                  photoInputRef.current?.click();
                }}
              >
                Choose from this device
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setPhotoMenuAnchor(null);
                  setPhotoQrOpen(true);
                }}
              >
                Upload from phone (QR)
              </MenuItem>
            </Menu>
            <PhotoQrDialog open={photoQrOpen} onClose={closePhotoQr} onPhoto={onPhotoFromQr} />
          </Box>
        </Grid>
      )}

      <Grid item xs={12} md={showPhoto ? 10 : 12}>
        <Grid container spacing={2}>
          <ConfigurableField sectionId={S.personal} fieldName="title" xs={6} sm={3} md={2}>
            {({ required }) => (
              <FormField label="Title" required={required}>
                <IpdTextField select fullWidth size="small" value={patient.title} onChange={(e) => updateField("title", e.target.value)}>
                  {TITLES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="name" xs={12} sm={9} md={4}>
            {({ required }) => (
              <FormField label="Name" required={required} error={errors.name}>
                <IpdTextField fullWidth size="small" value={patient.name} onChange={(e) => updateField("name", e.target.value)} error={Boolean(errors.name)} />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="patientType" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Patient Type" required={required} error={errors.patientType}>
                <IpdTextField select fullWidth size="small" value={patient.patientType} onChange={(e) => updateField("patientType", e.target.value)} error={Boolean(errors.patientType)}>
                  {PATIENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, " ")}</MenuItem>)}
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>

          <ConfigurableField sectionId={S.personal} fieldName="gender" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Gender" required={required} error={errors.gender}>
                <IpdTextField select fullWidth size="small" value={patient.gender} onChange={(e) => updateField("gender", e.target.value)} error={Boolean(errors.gender)}>
                  <MenuItem value="">Select</MenuItem>
                  {GENDERS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="dob" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Date of Birth" required={required} error={errors.dob}>
                <IpdDateField
                  value={patient.dob}
                  maxDate={new Date()}
                  onChange={(dob) => {
                    const ageVal = calcAge(dob);
                    setPatient((s) => ({
                      ...s,
                      dob,
                      ageOnly: ageVal != null ? String(ageVal) : s.ageOnly,
                    }));
                  }}
                  error={Boolean(errors.dob)}
                />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="ageOnly" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Age (if DOB unknown)" required={required}>
                <IpdTextField
                  fullWidth
                  size="small"
                  inputMode="numeric"
                  placeholder="years"
                  value={patient.ageOnly || ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    const years = parseInt(v || "0", 10);
                    if (years > 0) {
                      setPatient((s) => ({ ...s, ageOnly: v, dob: dobFromAgeYears(years) }));
                    } else {
                      updateField("ageOnly", v);
                    }
                  }}
                />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="maritalStatus" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Marital Status" required={required}>
                <IpdTextField select fullWidth size="small" value={patient.maritalStatus} onChange={(e) => updateField("maritalStatus", e.target.value)}>
                  <MenuItem value="">Select</MenuItem>
                  {MARITAL_STATUSES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="bloodGroup" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Blood Group" required={required}>
                <IpdTextField select fullWidth size="small" value={patient.bloodGroup} onChange={(e) => updateField("bloodGroup", e.target.value)}>
                  <MenuItem value="">Select</MenuItem>
                  {BLOOD_GROUPS.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>

          <ConfigurableField sectionId={S.personal} fieldName="occupation" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Occupation" required={required}>
                <IpdTextField fullWidth size="small" value={patient.occupation} onChange={(e) => updateField("occupation", e.target.value)} />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="nationality" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Nationality" required={required}>
                <IpdTextField select fullWidth size="small" value={patient.nationality} onChange={(e) => updateField("nationality", e.target.value)}>
                  {NATIONALITIES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="religion" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Religion" required={required}>
                <IpdTextField select fullWidth size="small" value={patient.religion} onChange={(e) => updateField("religion", e.target.value)}>
                  <MenuItem value="">Select</MenuItem>
                  {RELIGIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </IpdTextField>
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="identityMarks" xs={12} sm={6} md={3}>
            {({ required }) => (
              <FormField label="Identity Marks" required={required}>
                <IpdTextField fullWidth size="small" placeholder="Eg. mole on right cheek" value={patient.identityMarks} onChange={(e) => updateField("identityMarks", e.target.value)} />
              </FormField>
            )}
          </ConfigurableField>

          <ConfigurableField sectionId={S.personal} fieldName="familyPhysicianName" xs={12} sm={6} md={4}>
            {({ required }) => (
              <FormField label="Family Physician Name" required={required}>
                <IpdTextField fullWidth size="small" value={patient.familyPhysicianName} onChange={(e) => updateField("familyPhysicianName", e.target.value)} />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="familyPhysicianPhone" xs={12} sm={6} md={4}>
            {({ required }) => (
              <FormField label="Family Physician Phone" required={required}>
                <IpdTextField fullWidth size="small" value={patient.familyPhysicianPhone} onChange={(e) => updateField("familyPhysicianPhone", e.target.value)} />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="primaryMobile" xs={12} sm={6} md={4}>
            {({ required }) => (
              <FormField label="Primary Mobile" required={required} error={errors.primaryMobile}>
                <IpdTextField
                  fullWidth
                  size="small"
                  placeholder="10-digit mobile"
                  value={patient.primaryMobile}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPatient((s) => (
                      contactsSectionActive && isFieldActive(S.personal, "primaryMobile")
                        ? syncPersonalMobileToContacts(s, "primaryMobile", value)
                        : { ...s, primaryMobile: value }
                    ));
                  }}
                  error={Boolean(errors.primaryMobile)}
                />
              </FormField>
            )}
          </ConfigurableField>
          <ConfigurableField sectionId={S.personal} fieldName="secondaryMobile" xs={12} sm={6} md={4}>
            {({ required }) => (
              <FormField label="Secondary Mobile" required={required}>
                <IpdTextField
                  fullWidth
                  size="small"
                  value={patient.secondaryMobile}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPatient((s) => (
                      contactsSectionActive && isFieldActive(S.personal, "secondaryMobile")
                        ? syncPersonalMobileToContacts(s, "secondaryMobile", value)
                        : { ...s, secondaryMobile: value }
                    ));
                  }}
                />
              </FormField>
            )}
          </ConfigurableField>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default memo(PersonalInfo);
